import React, { useMemo, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Task, TaskStatus, VisibleColumnSpec } from "@/types";
import { useTranslation } from "react-i18next";
import { ColumnDef, Row, SortingState } from "@tanstack/react-table";
import { Anchor, Group } from "@mantine/core";
import { useGetOpenTasks } from "@/services/Tasks";
import { useVisibleColumns } from "@/hooks/useVisibleColumns";
import SortableHeader from "@/components/molecules/SortableHeader";
import NonSortableHeader from "@/components/molecules/NonSortableHeader";
import Badge from "@/components/atoms/Badge";
import TaskDrawer from "@/components/organisms/TaskDrawer";
import SVGAsset from "@/components/atoms/SVGAsset/SVGAsset";
import { useOrdersDrawerStore } from "@/stores/ordersDrawerStore";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
dayjs.extend(advancedFormat);
import DataTable from "@/components/molecules/DataTable";
import CTA from "@/components/atoms/CTA";
import NumberFormatter from "@/components/atoms/NumberFormatter";
import TaskTypeEnum from "@/constants/enums/TaskTypeEnum";

import styles from "../InvoiceTable/styles.module.css";

interface TasksTableProps {
  /** Define a column spec of what rows and columns to display and when **/
  visibleColumns?: VisibleColumnSpec<Task, "actions" | "statusIcon" | "mobile-actions">[];
  /** Define a predefined sort order **/
  initialSort?: SortingState;
  /** Whether to show client-side pagination */
  pagination?: boolean;
  /** Decide whether to enable or disable actions **/
  actionable?: boolean;
  /** Optional rows per page for client-side pagination */
  initialRowsPerPage?: number;
}

/**
 * A reusable task table component, with sorting, filtering, actions and pagination.
 * @param TaskTableProps - Component props
 * @param visibleColumns - Constant Array defined to manage which columns to display and when
 * @param initialSort - Define a predefined sort order
 * @param pagination - Boolean to enable or disable pagination
 * @param actionable - Boolean to enable or disable row actions
 * @param initialRowsPerPage - set maximum number of rows to display used for snapshots
 *
 **/
const TasksTable: React.FC<TasksTableProps> = ({
  visibleColumns,
  initialSort = null,
  pagination = true,
  actionable = true,
  initialRowsPerPage = 15,
}) => {
  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const [selectedRow, setSelectedRow] = useState<Task | undefined>(undefined);
  const openOrdersDrawer = useOrdersDrawerStore((s) => s.open);

  const { currentCompany } = useAuth();

  const { data: tasks } = useGetOpenTasks(currentCompany?.companyId as number, undefined, {
    enabled: !!currentCompany?.companyId,
  });
  const { t } = useTranslation();
  const currentDate = dayjs().format();

  const returnStatusLabel = (status: TaskStatus) => {
    const translationKey = "enumerables:taskStatus." + status;
    const text = t(translationKey);

    switch (status) {
      case "COMPLETED":
        return (
          <Badge color={"completed"} radius={"xs"}>
            {text}
          </Badge>
        );
      case "IN_PROGRESS":
        return <Badge color={"warning"}>{text}</Badge>;
      case "NEW":
        return (
          <Badge color={"primary"} radius={"xs"} size={"lg"}>
            {text}
          </Badge>
        );
      default:
        return <></>;
    }
  };

  const baseColumns: ColumnDef<Task>[] = useMemo(
    () => [
      {
        header: () => <span />,
        id: "statusIcon",
        size: 50,
        minSize: 50,
        maxSize: 50,
        cell: ({ row }) => {
          const isOverdue = dayjs(dayjs(row.original.dueDate)).isBefore(currentDate);
          return isOverdue ? (
            <Group justify={"center"}>
              <SVGAsset name={"warning-triangle"} width={24} height={24} />
            </Group>
          ) : (
            <></>
          );
        },
        meta: {
          breakpoint: "mantine-visible-from-sm",
        },
      },
      {
        header: ({ column }) => <SortableHeader column={column} label={t("tasks:dueDate")} />,
        id: "dueDate",
        accessorKey: "dueDate",
        cell: ({ row }) => {
          return dayjs(row.original.dueDate).format("Do MMMM YYYY, HH:mm");
        },
        meta: {
          breakpoint: "",
        },
      },
      {
        header: ({ column }) => <SortableHeader column={column} label={t("tasks:type")} />,
        id: "type",
        accessorKey: "type",
        cell: ({ row }) => {
          return t("enumerables:taskTypes." + row.original.type);
        },
        meta: {
          breakpoint: "",
        },
      },
      {
        header: ({ column }) => <SortableHeader column={column} label={t("tasks:counterParty")} />,
        id: "counterParty",
        accessorKey: "counterParty",
        meta: {
          breakpoint: "mantine-visible-from-md",
        },
      },
      {
        header: ({ column }) => <SortableHeader column={column} label={t("tasks:salesItem")} />,
        id: "salesItem",
        accessorKey: "salesItem",
        meta: {
          breakpoint: "mantine-visible-from-md",
        },
        cell: ({ row }) => row.original.salesItem?.toUpperCase(),
      },
      {
        header: ({ column }) => <SortableHeader column={column} label={t("common:amount")} />,
        id: "amount",
        accessorKey: "amount",
        meta: {
          breakpoint: "tablet",
        },
        cell: ({ row }) => <NumberFormatter value={row.original.amount ? Number(row.original.amount) : ""} />,
      },
      {
        header: ({ column }) => <SortableHeader column={column} label={t("common:order")} />,
        id: "id",
        meta: {
          breakpoint: "mantine-visible-from-lg",
        },
        cell: ({ row }) => row.original.orderIds.join(", "),
      },
      {
        header: ({ column }) => <SortableHeader column={column} label={t("tasks:contract")} />,
        meta: {
          breakpoint: "mantine-visible-from-lg",
        },
        id: "connected",
        cell: ({ row }) => (
          <>
            {row.original.type === TaskTypeEnum.APPROVE_CONTRACT ? (
              <Anchor onClick={() => window.location.replace("/contracts/details/" + row.original.connected)}>
                {row.original.connected}
              </Anchor>
            ) : null}
          </>
        ),
      },
      {
        header: ({ column }) => <NonSortableHeader column={column} label={t("tasks:status")} />,
        id: "status",
        cell: ({ row }) => {
          return returnStatusLabel(row.original.status);
        },
        maxSize: 100,
      },
      {
        id: "mobile-actions",
        header: "",
        meta: {
          breakpoint: "",
        },
        cell: ({ row }) => {
          return (
            <CTA fullWidth={true} type={"button"} intent={"secondary"} onClick={() => onRowClick(row)}>
              {t("actions:showMore")}
            </CTA>
          );
        },
      },
    ],
    [t, currentDate]
  );

  const columns = useVisibleColumns<Task, "statusIcon" | "actions" | "mobile-actions">(baseColumns, visibleColumns);

  const onClose = () => {
    setOpenDrawer(false);
    setSelectedRow(undefined);
  };

  const opensOrderDrawer = (type: Task["type"]) =>
    type === TaskTypeEnum.APPROVE_ORDER || type === TaskTypeEnum.PROCESS_ORDER;

  const onRowClick = (row: Row<Task> | undefined) => {
    const task = row?.original;
    if (task && opensOrderDrawer(task.type)) {
      openOrdersDrawer({ orderId: task.connected, tab: "details" });
      return;
    }
    setSelectedRow(task);
    setOpenDrawer(true);
  };

  return (
    <Group dir={"col"} className={styles.tableContainer}>
      <DataTable
        onRowClick={actionable ? onRowClick : undefined}
        data={tasks || []}
        columns={columns}
        isLoading={false}
        mobileHeaderCell={1}
        showPagination={pagination}
        initialSort={initialSort ?? undefined}
        initialRowsPerPage={initialRowsPerPage}
      />
      <TaskDrawer isOpen={openDrawer} onClose={onClose} isLoading={false} task={selectedRow} />
    </Group>
  );
};

export default TasksTable;
